import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { ValidatorRuleType } from "@tKgpco/Common/Utilities/validate";
import { useForm } from "@tKgpco/Custom/Hooks/useForm";
import editTerm from "@tKgpco/Handlers/Terms/UpdateTerm";
import { UpdateTermApiParameters } from "@tKgpco/Services/TermsService";
import { QuoteTerm } from "@tKgpco/Store/Data/Terms/TermsState";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    editTerm,
};

interface CustomWidgetStyles {
    mainGridContainer?: GridContainerProps;
    heading?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    mainGridContainer: {
        offsetCss: css`
            max-width: 700px;
            margin: 0 auto;
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                );
            }}
        `,
    },
    heading: {
        css: css`
            text-transform: uppercase;
            font-size: 22px;
            color: #0b70b7;
            border-bottom: 1px solid #0b70b7;
            padding-bottom: 5px;
            width: 100%;
        `,
    },
};

const EMPTY_STRING = "";

interface TermsFormValues {
    termsCode: string;
    vendor: string;
    type: string;
    estimate: boolean;
    description: string;
    default: boolean;
}

const validatorConfig: { [key in keyof TermsFormValues]: ValidatorRuleType } = {
    termsCode: {
        required: true,
        label: "TermsCode",
        requiredMsg: "Terms Code is required",
    },
    vendor: {
        required: true,
        label: "Vendor",
        requiredMsg: "Vendor is required",
    },
    type: {
        required: true,
        label: "Type",
        requiredMsg: "Type is required",
    },
    estimate: {
        required: false,
        label: "Estimate",
    },
    description: {
        required: true,
        label: "Description",
        requiredMsg: "Description is required",
    },
    default: {
        required: false,
        label: "Default",
    },
};

interface OwnProps {
    quoteTerm: QuoteTerm;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const EditTermsForm: React.FC<Props> = ({ editTerm, quoteTerm, history }) => {
    const {
        fieldChangeHandler,
        validateField,
        updateFormState,
        updateFormErrors,
        formState,
        formErrors,
        isSubmitDisabled,
    } = useForm({
        formValues: {
            termsCode: quoteTerm.termsCode,
            vendor: quoteTerm.vendor,
            type: quoteTerm.type,
            estimate: quoteTerm.estimate,
            description: quoteTerm.description,
            default: quoteTerm.default
        },
        initialErrorState: {},
        validatorConfig,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const handleUpdate = () => {
        setIsSubmitting(true);
        const params: UpdateTermApiParameters = {
            Id: quoteTerm.id,
            TermsCode: formState.termsCode,
            Vendor:formState.vendor,
            Type:formState.type,
            Estimate:formState.estimate,
            Description:formState.description,
            Default:formState.default
        };
        editTerm({
            ...params,
            onSuccess: () => {
                setIsSubmitting(false);
                history.push("/MyAccount/Terms");
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Term updated successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };
    
    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem width={12}>
                <Typography as="h2" {...styles.heading}>
                    {translate("Update Term")}
                </Typography>
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Code")}
                    name="termsCode"
                    value={formState.termsCode}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.termsCode && formErrors.termsCode}
                    iconProps={{
                        color: "danger",
                        src: formErrors.termsCode ? AlertTriangle : undefined,
                    }}
                    required
                    disabled
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Vendor Item Id")}
                    name="vendor"
                    value={formState.vendor}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.vendor && formErrors.vendor}
                    iconProps={{
                        color: "danger",
                        src: formErrors.vendor ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Type")}
                    name="type"
                    value={formState.type}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.type && formErrors.type}
                    iconProps={{
                        color: "danger",
                        src: formErrors.type ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Description")}
                    name="description"
                    value={formState.description}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.description && formErrors.description}
                    iconProps={{
                        color: "danger",
                        src: formErrors.description ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.estimate}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            estimate: !formState.estimate,
                        }))
                    }
                >
                    {translate("Estimate")}
                </Checkbox>
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.default}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            default: !formState.default,
                        }))
                    }
                >
                    {translate("Default")}
                </Checkbox>
            </GridItem>
            <GridItem>
                <Button onClick={handleUpdate} disabled={isSubmitDisabled || isSubmitting}>
                    Submit
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default connect(null, mapDispatchToProps)(withHistory(EditTermsForm));
